import { h } from "vue";
import { createRouter, createWebHistory, RouterView } from "vue-router";
import Home from "@/applications/skolebordetv2/views/HomeView.vue";
import { SessionHelper } from "@/helpers/utilities/sessionHelper";
import { useFeaturesStore } from "@/stores/features";
import { useSettingsStore } from "@/stores/settings";
import { useMeStore } from "@/stores/me";

let i18n;
let timeoutObj;

const RouterViewShell = { render: () => h(RouterView) };

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Tile menus
    {
      path: "/",
      name: "home",
      component: Home,
      props: true
    },
    {
      path: "/tiles/:groupId",
      name: "tiles",
      component: Home,
      props: true
    },

    // Sections
    {
      path: "/sections",
      component: RouterViewShell,
      children: [
        {
          path: "",
          name: "sections",
          component: () => import("@/views/Section/SectionsView.vue")
        },
        {
          path: ":sectionId",
          component: RouterViewShell,
          children: [
            {
              path: "",
              name: "section",
              component: () => import("@/views/Section/SectionView.vue"),
              props: true
            },
            {
              path: "links",
              name: "sectionlinks",
              component: () => import("@/views/Section/SectionLinksView.vue"),
              props: true
            },
            {
              path: "materials/:groupId?/:itemId?",
              name: "sectionMaterials",
              component: () => import("@/views/Section/SectionMaterialsView.vue"),
              props: route => ({
                sectionId: route.params.sectionId,
                groupId: route.params.groupId || undefined,
                itemId: route.params.itemId || undefined
              })
            },

            // Confidential v3 — static "children" segments first, optional-param catch-all last
            {
              path: "conf/children",
              name: "confidentialChildrenView",
              component: () => import("@/views/Documents/V2/ChildrenView.vue"),
              props: true,
              beforeEnter: twoFactorGuard
            },
            {
              path: "conf/children/:childId/:groupId?/:itemId?",
              name: "confidentialsChildView",
              component: () => import("@/views/Documents/V2/ChildView.vue"),
              props: route => ({
                sectionId: route.params.sectionId,
                childId: route.params.childId,
                groupId: route.params.groupId || undefined,
                itemId: route.params.itemId || undefined
              }),
              beforeEnter: twoFactorGuard
            },
            {
              path: "conf/:groupId?/:itemId?",
              name: "confidentialsSectionView",
              component: () => import("@/views/Documents/V2/SectionView.vue"),
              props: route => ({
                sectionId: route.params.sectionId,
                groupId: route.params.groupId || undefined,
                itemId: route.params.itemId || undefined
              }),
              beforeEnter: twoFactorGuard
            },

            // Assignments (teacher)
            {
              path: "assignments",
              component: RouterViewShell,
              children: [
                {
                  path: "",
                  name: "sectionAssignments",
                  component: () => import("@/views/Assignments/Assignments.vue"),
                  meta: { roles: ["isTeacher", "isSuperuser"], redirects: { isStudent: "sectionAssignmentsStudents" } },
                  props: true
                },
                {
                  path: "new",
                  name: "sectionCreateAssignment",
                  component: () => import("@/views/Assignments/Assignment.vue"),
                  meta: {
                    roles: ["isTeacher", "isSuperuser"],
                    redirects: { isStudent: "sectionAssignmentSubmission" }
                  },
                  props: true
                },
                {
                  path: ":assignmentId",
                  component: RouterViewShell,
                  children: [
                    {
                      path: "",
                      name: "sectionAssignment",
                      component: () => import("@/views/Assignments/Assignment.vue"),
                      meta: {
                        roles: ["isTeacher", "isSuperuser"],
                        redirects: { isStudent: "sectionAssignmentSubmission" }
                      },
                      props: true
                    },
                    {
                      path: "submission/:submissionId",
                      name: "sectionAssignmentSubmissionOutcome",
                      component: () => import("@/views/Assignments/Outcome.vue"),
                      meta: { roles: ["isTeacher", "isSuperuser"], redirects: { isStudent: "home" } },
                      props: true
                    }
                  ]
                }
              ]
            },

            // Assignments (student)
            {
              path: "student/assignments",
              component: RouterViewShell,
              children: [
                {
                  path: "",
                  name: "sectionAssignmentsStudents",
                  component: () => import("@/views/Assignments/Students/Assignments.vue"),
                  meta: {
                    roles: ["isStudent"],
                    redirects: { isTeacher: "sectionAssignments", isSuperuser: "sectionAssignments" }
                  },
                  props: true
                },
                {
                  path: ":assignmentId/submission",
                  name: "sectionAssignmentSubmission",
                  component: () => import("@/views/Assignments/Students/Submission.vue"),
                  meta: {
                    roles: ["isStudent"],
                    redirects: { isTeacher: "sectionAssignment", isSuperuser: "sectionAssignment" }
                  },
                  props: true
                }
              ]
            }
          ]
        }
      ]
    },

    // Top-level assignments (teacher overview across all sections)
    {
      path: "/assignments",
      name: "assignments",
      component: () => import("@/views/Assignments/Assignments.vue"),
      meta: { roles: ["isTeacher", "isSuperuser"], redirects: { isStudent: "sectionAssignmentsStudents" } },
      props: true
    },

    // CombiDrive
    {
      path: "/combidrive/:systemId/:entityId/:groupId/:itemId?",
      name: "combidrive",
      component: () => import("@/views/Documents/V2/CombiDriveView.vue"),
      meta: { title: "Documents" },
      props: route => ({
        systemId: route.params.systemId,
        entityId: route.params.entityId,
        groupId: route.params.groupId,
        itemId: route.params.itemId || undefined
      })
    },

    // People / confidential
    {
      path: "/people",
      component: RouterViewShell,
      children: [
        {
          path: "unregistered",
          name: "confidentialChildrenUnregisteredView",
          component: () => import("@/views/people/confidential/PeopleUnregisteredView.vue"),
          props: true,
          meta: { roles: ["isSuperuser"], redirects: { isStudent: "home", isTeacher: "home" } },
          beforeEnter: twoFactorGuard
        },
        {
          path: "unregistered/:childId/:groupId?/:itemId?",
          name: "confidentialsChildUnregisteredView",
          component: () => import("@/views/Documents/V2/ChildView.vue"),
          props: route => ({
            childId: route.params.childId,
            groupId: route.params.groupId || undefined,
            itemId: route.params.itemId || undefined
          }),
          meta: { roles: ["isSuperuser"], redirects: { isStudent: "home", isTeacher: "home" } },
          beforeEnter: twoFactorGuard
        },
        {
          path: "access",
          name: "confidentialPeopleView",
          component: () => import("@/views/people/confidential/PeopleAccessView.vue"),
          props: true,
          meta: { roles: ["isSuperuser"], redirects: { isStudent: "home", isTeacher: "home" } },
          beforeEnter: twoFactorGuard
        },
        {
          path: "access/:personId",
          name: "confidentialPeopleAccessView",
          component: () => import("@/views/people/confidential/PersonAccessView.vue"),
          props: true,
          meta: { roles: ["isSuperuser"], redirects: { isStudent: "home", isTeacher: "home" } },
          beforeEnter: twoFactorGuard
        }
      ]
    },

    // Documents
    {
      path: "/documents/:driveId?/:itemId?",
      name: "documents",
      component: () => import("@/views/Documents/DocumentsView.vue"),
      meta: { title: "Documents" },
      props: route => ({
        driveId: route.params.driveId || undefined,
        itemId: route.params.itemId || undefined
      })
    },
    {
      path: "/mydocuments/:groupId?/:itemId?/:driveId?",
      name: "mydocuments",
      component: () => import("@/views/Documents/MyDocumentsView.vue"),
      meta: { title: "MyDocuments" },
      props: route => ({
        groupId: route.params.groupId || undefined,
        driveId: route.params.driveId || undefined,
        itemId: route.params.itemId || undefined
      })
    },

    // Links
    {
      path: "/links",
      name: "links",
      component: () => import("@/views/Links/LinksView.vue")
    },

    // Integrations
    {
      path: "/os2faktorlogin/:to?/:from?",
      name: "os2FaktorLogin",
      component: () => import("@/views/Integrations/os2FaktorLoginView"),
      props: route => ({
        to: route.params.to || undefined,
        from: route.params.from || undefined
      })
    },

    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/NotFound.vue")
    }
  ]
});

router.beforeEach(async (to, from, next) => {
  const sessionToken = SessionHelper.nctSessionToken();

  if (!sessionToken) {
    return;
  }

  // Manually invoke the leaf route's `beforeEnter` guard so it runs even on
  // tab-style param-only navigations (where Vue Router skips it). With nested
  // routes `to.matched.at(-1)` is always the leaf; `matched[0]` would be an
  // outer pass-through with no guard. Track whether the inner guard called
  // `next()` itself to avoid the "next called more than once" warning.
  let beforeEnterCalledNext = false;
  const beforeEnterNext = arg => {
    beforeEnterCalledNext = true;
    next(arg);
  };
  await to.matched.at(-1)?.beforeEnter?.(to, from, beforeEnterNext);
  if (beforeEnterCalledNext) {
    return;
  }

  if (to.meta.roles) {
    const meStore = useMeStore();

    const allowedRoles = to.meta.roles.find(r => meStore[r]);

    if (!allowedRoles) {
      if (meStore.isSuperuser) {
        return next({ name: to.meta.redirects.isSuperuser, params: to.params });
      }

      if (meStore.isTeacher) {
        return next({ name: to.meta.redirects.isTeacher, params: to.params });
      }

      if (meStore.isStudent) {
        return next({ name: to.meta.redirects.isStudent, params: to.params });
      }
    }
  }

  const browserTitle = document.title.split(" - ")[0];

  if (browserTitle) {
    const nearestWithTitle = to.matched
      .slice()
      .reverse()
      .find(r => r.meta?.title);

    document.title =
      browserTitle + (nearestWithTitle && i18n ? " - " + i18n.global.t("Routes." + nearestWithTitle.meta.title) : "");
  }

  next();
});

router.afterEach(() => {
  clearTimeout(timeoutObj);

  timeoutObj = setTimeout(() => {
    SessionHelper.logout();
  }, 3600000);
});

/**
 * Set translation
 * @param { object } i i18n translation object
 */
export function setTranslater(i) {
  i18n = i;
}

/**
 * Check for two factor authentication
 * @param { object } to To route
 * @param { object } from From route
 * @param { (location?: object) => void } next Next function
 * @returns { Promise<void> } Resolves once the navigation guard has dispatched
 */
async function twoFactorGuard(to, from, next) {
  const featuresStore = useFeaturesStore();
  const settingsStore = useSettingsStore();

  if (featuresStore.isFeatureEnabled("OS2Faktor")) {
    return await import("@/helpers/integrations/os2FaktorHelper").then(os2FaktorHelper => {
      if (os2FaktorHelper.isSecondAuthentication(settingsStore.setting("os2faktorSessionTimeout"))) {
        return next();
      }
      router.push({ name: "os2FaktorLogin", params: { to: to.path, from: from.path } });
      return false;
    });
  } else {
    return next();
  }
}
